import { useMemo, useState } from 'react';
import { css } from '@emotion/react';
import type {
  SourceDeploymentConfig,
  SyncEngineDeploymentInfo,
} from '@weave/schema-gen-ts/dist/schemas/integrations/sync-config-api/v1/message.pb';
import { useUpdateSourceDeploymentConfigMutation } from '@frontend/api-sync-config';
import { Button, Table, Text, useAlert, useModalControl } from '@frontend/design-system';
import { ActionModal, ActionModalContent } from '../shared/action-modal';
import {
  buildSourceDeploymentConfigRequest,
  getDisplayValue,
  getEngineOverride,
  getErrorMessage,
} from '../shared/helpers';
import { EngineOverrideKind } from '../shared/types';
import { EditSourcePolicyModal } from './edit-source-policy-modal';
import { PinEngineModal } from './pin-engine-modal';

type SourceConfigOverrides = Parameters<typeof buildSourceDeploymentConfigRequest>[2];

interface SourceDetailViewProps {
  canManage: boolean;
  config: SourceDeploymentConfig;
  onBack: () => void;
}

export const SourceDetailView = ({ canManage, config, onBack }: SourceDetailViewProps) => {
  const alerts = useAlert();
  const editPolicyModal = useModalControl();
  const pinEngineModal = useModalControl();
  const removeEnginePinModal = useModalControl();
  const [engineToPin, setEngineToPin] = useState<SyncEngineDeploymentInfo>();
  const [engineToUnpin, setEngineToUnpin] = useState<SyncEngineDeploymentInfo>();

  const { mutate: updateSourcePolicy, isLoading: isUpdatingSourcePolicy } =
    useUpdateSourceDeploymentConfigMutation<Error>({
      httpOptions: { skipValidation: true },
    });

  const engineRows = useMemo<SyncEngineDeploymentInfo[]>(() => {
    const containerRow: SyncEngineDeploymentInfo[] = config.containerVersionInfo
      ? [{ syncEngineId: 'Container', versionInfo: config.containerVersionInfo }]
      : [];
    const syncEngineRows = (config.syncEngines ?? []).map((engine) => ({
      syncEngineId: engine.syncEngineId ?? 'N/A',
      versionInfo: engine.versionInfo,
    }));

    return [...containerRow, ...syncEngineRows];
  }, [config.containerVersionInfo, config.syncEngines]);

  const updateSourceConfig = (
    overrides: SourceConfigOverrides,
    { successMessage, errorPrefix, onSuccess }: { successMessage: string; errorPrefix: string; onSuccess: () => void }
  ) => {
    if (!canManage || !config.sourceId || isUpdatingSourcePolicy) return;

    updateSourcePolicy(buildSourceDeploymentConfigRequest(config.sourceId, config, overrides), {
      onSuccess: () => {
        onSuccess();
        alerts.success(successMessage);
      },
      onError: (error: Error) => {
        alerts.error(`${errorPrefix}: ${getErrorMessage(error)}`);
      },
    });
  };

  const handleUpdatePolicy = (channelName: string, trainId: string) =>
    updateSourceConfig(
      { targetChannel: channelName, targetTrainId: trainId },
      {
        successMessage: `Source policy updated to channel ${channelName} and train ${trainId}`,
        errorPrefix: 'Failed to update source policy',
        onSuccess: editPolicyModal.closeModal,
      }
    );

  const handlePinEngineClick = (engine: SyncEngineDeploymentInfo) => {
    if (!canManage || !engine.versionInfo?.packageName) return;

    setEngineToPin(engine);
    pinEngineModal.openModal();
  };

  const handlePinEngine = (engine: SyncEngineDeploymentInfo, versionTag: string) =>
    updateSourceConfig(
      { enginePin: { engine, targetVersion: versionTag } },
      {
        successMessage: `Pinned ${engine.syncEngineId} to ${versionTag}`,
        errorPrefix: `Failed to pin ${engine.syncEngineId}`,
        onSuccess: () => {
          pinEngineModal.closeModal();
          setEngineToPin(undefined);
        },
      }
    );

  const handleRemovePinClick = (engine: SyncEngineDeploymentInfo) => {
    if (!canManage) return;

    setEngineToUnpin(engine);
    removeEnginePinModal.openModal();
  };

  const handleRemovePin = (engine: SyncEngineDeploymentInfo) =>
    updateSourceConfig(
      { enginePin: { engine, targetVersion: '' } },
      {
        successMessage: `Removed pin from ${engine.syncEngineId}`,
        errorPrefix: `Failed to remove pin from ${engine.syncEngineId}`,
        onSuccess: () => {
          removeEnginePinModal.closeModal();
          setEngineToUnpin(undefined);
        },
      }
    );

  return (
    <div css={wrapperStyle}>
      <div css={headerRowStyle}>
        <Button iconName='arrow-left' hoverLabel='Back to sources' onClick={onBack} variant='tertiary' />
        <div css={titleBlockStyle}>
          <Text as='h2' weight='bold' size='large'>
            Source Details
          </Text>
        </div>
      </div>

      <dl css={summaryStyle}>
        <SummaryItem label='Source ID' value={config.sourceId} />
        <SummaryItem label='Train ID' value={config.targetTrainId} />
        <SummaryItem label='Campaign Override ID' value={config.campaignOverrideId} />
        <ChannelSummaryItem value={config.targetChannel} onEdit={editPolicyModal.openModal} disabled={!canManage} />
      </dl>

      <div css={sectionStyle}>
        <Text as='h3' weight='bold'>
          Container and Sync Engines
        </Text>
        <Table
          data={engineRows}
          colConfig={[
            {
              Header: 'Package',
              id: 'packageName',
              accessor: (row: SyncEngineDeploymentInfo) => row.versionInfo?.packageName,
              cellRenderer: (value?: string) => <Text>{getDisplayValue(value)}</Text>,
              minWidth: 180,
            },
            {
              Header: 'Sync Engine ID',
              id: 'engineId',
              accessor: (row: SyncEngineDeploymentInfo) => row.syncEngineId,
              cellRenderer: (value: string) => <Text weight='bold'>{value}</Text>,
              minWidth: 220,
            },
            {
              Header: 'Running Version',
              id: 'runningVersion',
              accessor: (row: SyncEngineDeploymentInfo) => row.versionInfo?.runningVersion,
              cellRenderer: (value?: string) => <Text>{getDisplayValue(value)}</Text>,
              minWidth: 160,
            },
            {
              Header: 'Target Version',
              id: 'targetVersion',
              accessor: (row: SyncEngineDeploymentInfo) => row.versionInfo?.targetVersion,
              cellRenderer: (value?: string) => <Text>{getDisplayValue(value)}</Text>,
              minWidth: 160,
            },
            {
              Header: 'Status',
              id: 'status',
              accessor: (row: SyncEngineDeploymentInfo) => row.versionInfo?.status,
              cellRenderer: (value?: string) => <Text>{getDisplayValue(value)}</Text>,
              minWidth: 140,
            },
            {
              Header: 'Binary',
              id: 'binaryName',
              accessor: (row: SyncEngineDeploymentInfo) => row.versionInfo?.binaryName,
              cellRenderer: (value?: string) => <Text>{getDisplayValue(value)}</Text>,
              minWidth: 160,
            },
            {
              Header: 'Expected Signature',
              id: 'expectedSignature',
              accessor: (row: SyncEngineDeploymentInfo) => row.versionInfo?.expectedSignature,
              cellRenderer: (value?: string) => <Text>{getDisplayValue(value)}</Text>,
              minWidth: 220,
            },
            {
              Header: 'Overrides',
              id: 'overrides',
              accessor: (row: SyncEngineDeploymentInfo) => row.syncEngineId,
              cellRenderer: (_, row) => {
                const override = getEngineOverride(row?.versionInfo, config);

                return override.kind === EngineOverrideKind.ActivePin ? (
                  <span css={overrideChipStyle}>ACTIVE PIN : {override.version}</span>
                ) : (
                  <Text>{override.label}</Text>
                );
              },
              sticky: 'right',
            },
            {
              Header: 'Action',
              id: 'action',
              accessor: (row: SyncEngineDeploymentInfo) => row.syncEngineId,
              cellRenderer: (_, row) => {
                const override = getEngineOverride(row?.versionInfo, config);

                return override.kind === EngineOverrideKind.ActivePin ? (
                  <Button
                    variant='primary'
                    size='small'
                    destructive
                    disabled={!canManage}
                    hoverLabel={!canManage ? 'You do not have enough permissions' : undefined}
                    showLabelWhenDisabled={!canManage}
                    onClick={() => row && handleRemovePinClick(row)}
                  >
                    Remove Pin
                  </Button>
                ) : (
                  <Button
                    variant='tertiary'
                    size='small'
                    disabled={!canManage || !row?.versionInfo?.packageName}
                    hoverLabel={!canManage ? 'You do not have enough permissions' : undefined}
                    showLabelWhenDisabled={!canManage}
                    onClick={() => row && handlePinEngineClick(row)}
                  >
                    Pin Engine
                  </Button>
                );
              },
              sticky: 'right',
            },
          ]}
        />
      </div>

      <EditSourcePolicyModal
        config={config}
        isSaving={isUpdatingSourcePolicy}
        modalProps={editPolicyModal.modalProps}
        onConfirm={handleUpdatePolicy}
      />
      <PinEngineModal
        engine={engineToPin}
        isSaving={isUpdatingSourcePolicy}
        modalProps={pinEngineModal.modalProps}
        onConfirm={handlePinEngine}
      />
      <ActionModal modalProps={removeEnginePinModal.modalProps} isSaving={isUpdatingSourcePolicy}>
        <ActionModalContent
          title='Remove Pin'
          onClose={removeEnginePinModal.closeModal}
          isSaving={isUpdatingSourcePolicy}
          confirmLabel='Remove Pin'
          savingLabel='Removing...'
          confirmDisabled={!engineToUnpin}
          destructive
          onConfirm={() => engineToUnpin && handleRemovePin(engineToUnpin)}
        >
          <Text>
            Remove the pin for <strong>{engineToUnpin?.syncEngineId ?? 'this engine'}</strong>?
          </Text>
          <div css={removePinDetailsStyle}>
            <Text size='small' color='subdued'>
              Package
            </Text>
            <Text weight='bold'>{getDisplayValue(engineToUnpin?.versionInfo?.packageName)}</Text>
            <Text size='small' color='subdued'>
              Pinned version
            </Text>
            <Text weight='bold'>{getDisplayValue(engineToUnpin?.versionInfo?.targetVersion)}</Text>
          </div>
        </ActionModalContent>
      </ActionModal>
    </div>
  );
};

interface SummaryItemProps {
  label: string;
  value?: string;
}

const SummaryItem = ({ label, value }: SummaryItemProps) => (
  <div css={summaryItemStyle}>
    <Text as='dt' size='small' color='subdued'>
      {label}
    </Text>
    <Text as='dd' weight='bold'>
      {getDisplayValue(value)}
    </Text>
  </div>
);

const ChannelSummaryItem = ({
  value,
  onEdit,
  disabled,
}: Pick<SummaryItemProps, 'value'> & { onEdit: () => void; disabled: boolean }) => {
  const channel = value?.trim();

  return (
    <div css={[summaryItemStyle, channelSummaryItemStyle]}>
      <Text as='dt' size='small' color='subdued'>
        Target Channel
      </Text>
      <dd css={summaryValueStyle}>{channel ? <span css={channelChipStyle}>{channel.toUpperCase()}</span> : 'N/A'}</dd>
      <Button
        css={editPolicyButtonStyle}
        type='button'
        variant='tertiary'
        size='small'
        disabled={disabled}
        hoverLabel={disabled ? 'You do not have enough permissions' : undefined}
        showLabelWhenDisabled={disabled}
        onClick={onEdit}
      >
        Edit Source Policy
      </Button>
    </div>
  );
};

const wrapperStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--fabric-space-gap-lg);
  padding: var(--fabric-space-padding-lg);
`;

const headerRowStyle = css`
  display: flex;
  align-items: center;
  gap: var(--fabric-space-gap-sm);
`;

const titleBlockStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--fabric-space-gap-xxs);
`;

const summaryStyle = css`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: var(--fabric-space-gap-lg);
  margin: 0;
  padding: var(--fabric-space-padding-lg);
  border: var(--fabric-border-width-thin) solid var(--fabric-color-neutral-20);
  border-radius: var(--fabric-radius-md);
`;

const summaryItemStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--fabric-space-gap-xxs);

  dd {
    margin: 0;
    overflow-wrap: anywhere;
  }
`;

const summaryValueStyle = css`
  margin: 0;
`;

const channelSummaryItemStyle = css`
  align-items: flex-end;
`;

const editPolicyButtonStyle = css`
  margin-top: var(--fabric-space-gap-sm);
`;

const channelChipStyle = css`
  display: inline-block;
  padding: var(--fabric-space-padding-xxs) var(--fabric-space-padding-sm);
  border: var(--fabric-border-width-thin) solid var(--fabric-color-neutral-30);
  border-radius: var(--fabric-radius-full);
  background-color: var(--fabric-color-neutral-5);
  color: var(--fabric-color-neutral-90);
  font-size: var(--fabric-font-size-xs);
  letter-spacing: var(--fabric-typography-body-xs-letter-spacing);
`;

const overrideChipStyle = css`
  display: inline-block;
  padding: var(--fabric-space-padding-xxs) var(--fabric-space-padding-sm);
  border: var(--fabric-border-width-thin) solid var(--fabric-color-green-30);
  border-radius: var(--fabric-radius-sm);
  background-color: var(--fabric-color-green-5);
  color: var(--fabric-color-green-50);
  font-size: var(--fabric-font-size-xs);
`;

const sectionStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--fabric-space-gap-md);
`;

const removePinDetailsStyle = css`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: var(--fabric-space-gap-xs) var(--fabric-space-gap-md);
  align-items: baseline;
`;
